<script setup>
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { use } from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import { LineChart } from 'echarts/charts'
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  ToolboxComponent,
  GridComponent,
  DataZoomComponent,
} from 'echarts/components'
import VChart from 'vue-echarts'
import * as echarts from 'echarts'
import Loader from '@/views/Loader.vue'
import axios from '@/helpers/axios'
import Breadcrumbs from '@/views/general/Breadcrumbs.vue'
import moment from "moment"
import { VCard } from "vuetify/components"

definePage({
  meta: { layout: 'default', role: 'user' },
})

use([
  CanvasRenderer,
  LineChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  ToolboxComponent,
  GridComponent,
  DataZoomComponent,
])

const { t } = useI18n()
const route = useRoute()
const uuid = route.params.uuid

const device = ref({})
const bread = ref([])
const ready = ref(false)
const loading = ref(false)

const startDate = ref(moment().subtract(7, 'days').format('YYYY-MM-DD'))
const endDate = ref(moment().format('YYYY-MM-DD'))

const chartRef = ref(null)
const dates = ref([])
const values = ref([])
const labels = ref([])

const options = ref({
  tooltip: { trigger: 'axis' },
  legend: { data: labels },
  xAxis: { type: 'category', boundaryGap: false, data: dates },
  yAxis: {
    type: 'value',
    boundaryGap: [0, '100%'],
    min: 'dataMin',
    max: 'dataMax',
  },
  dataZoom: [
    { type: 'inside', start: 0, end: 100 },
    { start: 0, end: 100 },
  ],
  series: values,
})

// Fetch device info
axios.get(`/devices/${uuid}`).then(res => {
  device.value = res.data
}).catch(console.error).finally(() => {
  ready.value = true
})

const getData = async () => {
  loading.value = true

  try {
    // preserve current legend selection
    let selected = {}
    const chartInstance = chartRef.value?.chart
    if (chartInstance) {
      selected = chartInstance.getOption()?.legend?.[0]?.selected || {}
    }

    const res = await axios.get(`/devices/${uuid}/all/data`, {
      params: { start: startDate.value, end: endDate.value },
    })

    // reset arrays before adding new data
    labels.value = []
    values.value = []
    dates.value = res.data.dates.map(date =>
      moment.utc(date).local().format("YYYY-MM-DD HH:mm")
    )

    Object.values(res.data.channels).forEach(val => {
      labels.value.push(val.name)
      values.value.push({
        name: val.name,
        data: val.values,
        type: 'line',
        sampling: 'lttb',
        connectNulls: true,
      })
    })

    // update options without resetting chart
    options.value.legend.data = labels.value
    options.value.xAxis.data = dates.value
    options.value.series = values.value

    // re-apply previous legend visibility
    if (chartInstance && Object.keys(selected).length) {
      chartInstance.setOption({ legend: { selected } }, false)
    }

  } catch (e) {
    console.error(e)
  } finally {
    loading.value = false
  }
}

getData()

watch([startDate, endDate], ([newStart, newEnd]) => {
  const s = moment(newStart, 'YYYY-MM-DD')
  const e = moment(newEnd, 'YYYY-MM-DD')
  if (s.isAfter(e)) startDate.value = e.format('YYYY-MM-DD')
  getData()
})

watchEffect(() => {
  bread.value = [
    { text: t('devices.title'), link: { name: 'devices-mine' } },
    { text: device.value.name || '...', link: { name: 'devices-uuid', params: { uuid: device.value.uuid } } },
    { text: t('devices.all_channels') },
  ]
})
</script>

<template>
  <div>
    <Breadcrumbs :items="bread" />

    <Loader v-if="!ready" />

    <VCard
      v-else
      class="mb-6 overflow-visible pa-4 d-flex flex-column"
      style="border-radius: 16px; height: 100%;"
      elevation="2"
    >
      <div class="d-flex justify-space-between align-start mb-4">
        <div>
          <h4 class="text-h5 font-weight-semibold mb-1 text-high-emphasis">
            {{ $t('devices.all_channels') }}
          </h4>
          <p class="text-body-1 text-medium-emphasis mb-0">
            {{ $t('devices.device') }}: <span class="font-weight-bold">{{ device.name }}</span>
          </p>
        </div>

        <VBtn
          icon
          size="small"
          variant="tonal"
          color="primary"
          :to="{ name: 'devices-uuid', params: { uuid: device.uuid } }"
        >
          <VIcon icon="tabler-arrow-left" size="x-large" />
          <VTooltip activator="parent" location="top">
            {{ $t('layout.go_back') }}
          </VTooltip>
        </VBtn>
      </div>

      <div class="mb-4">
        <VForm @submit.prevent="() => {}">
          <VRow>
            <VCol cols="12" md="6">
              <AppDateTimePicker
                v-model="startDate"
                :label="$t('devices.start_date')"
              />
            </VCol>

            <VCol cols="12" md="6">
              <AppDateTimePicker
                v-model="endDate"
                :label="$t('devices.end_date')"
              />
            </VCol>
          </VRow>
        </VForm>
      </div>

      <div>
        <Loader v-if="loading" />
        <VChart
          ref="chartRef"
          v-show="!loading"
          style="height: 500px;"
          :option="options"
          autoresize
        />
      </div>
    </VCard>
  </div>
</template>
